import React from "react";
import styled from "styled-components";

export interface InnerListItemProps
  extends React.HTMLAttributes<HTMLDivElement> {
  align?: "center" | "top";
  focused?: boolean;
  onSelect?: () => void;
  selected?: boolean;
  selectionType?: "multiple" | "single";
}

export interface ListItemProps extends InnerListItemProps {
  isSelected?: boolean;
  onSelectToggle?: (item?: unknown) => void;
}

const Indicator = styled.div`
  width: 0.3rem;
  transition: background-color 0.2s;
  margin-right: 0.3rem;
`;

const Styled = styled.div<ListItemProps>`
  display: flex;
  background-color: #fff;
  margin-top: 0.1rem;
  padding: 0.3rem;
  transition:
    border-color 0.2s,
    box-shadow 0.2s,
    background-color 0.2s;

  ${(props) => {
    if (props.onClick) {
      return `
        cursor: pointer;
      `;
    }
  }}

  &:first-child {
    margin-top: 0;
  }

  &:last-child {
    margin-bottom: 0;
  }

  ${(props) => {
    if (props.focused) {
      return `
        ${Indicator} {
          background-color: #90adc3;
        }
      `;
    }
  }}

  &:hover {
    .title {
      text-decoration: underline;
    }

    ${(props) => {
      if (props.onClick) {
        return `
        background-color: #fdfff1;
      `;
      }
    }}

    ${(props) => {
      if (props.onClick) {
        if (props.focused) {
          return `
        ${Indicator} {
          background-color: #7491a5;
        }
        `;
        } else {
          return `
        ${Indicator} {
          background-color: #ddd;
        }
      `;
        }
      }
    }}
  }
`;

const StyledListItemInput = styled.div<Props>`
  display: flex;
  cursor: pointer;

  & > div {
    height: 2.8rem;
    width: 2.6rem;
    transition:
      background-color 0.2s,
      box-shadow 0.2s;
    border-radius: 0.3rem;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  input {
    margin: 0;
    cursor: pointer;
    /* transition: box-shadow 0.2s; */
  }

  &:hover {
    & > div {
      background-color: #f0f0f0;
    }

    input {
      /* box-shadow: 0 0 0.5rem 0.2rem rgba(79, 78, 156, 0.2); */
    }
  }

  ${(props) => {
    if (props.align === "center") {
      return `
        align-items: center;
      `;
    }
  }}
`;

export interface Props extends React.HTMLAttributes<HTMLDivElement> {
  align?: "top" | "center";
}

/**
 * Contains the checkbox or radio button input and a padded container for the
 * input. The entire surface can be clicked to trigger a click of the input
 * itself.
 */
const ListItemInput: React.FC<React.PropsWithChildren<Props>> = (props) => {
  return <StyledListItemInput {...props}>{props.children}</StyledListItemInput>;
};

interface MainProps {
  align?: "top" | "center";
}

const Main = styled.div<MainProps>`
  flex: 1;
  display: flex;
  padding: 0.5rem;
  min-width: 0;
  gap: 1rem;

  ${(props) => {
    if (props.align === "center") {
      return `
        align-items: center;
      `;
    }
  }}
`;

const ListItem = React.forwardRef<HTMLDivElement, InnerListItemProps>(
  (props, ref) => {
    const { onSelect } = props;

    const onClick = React.useCallback(
      (e: { stopPropagation: () => void }) => {
        e.stopPropagation();
        onSelect?.();
      },
      [onSelect],
    );

    return (
      <Styled {...props} ref={ref}>
        {props.onClick && <Indicator />}
        {props.selectionType && props.onSelect && (
          <ListItemInput
            align={props.align}
            onClick={onSelect == null ? undefined : onClick}
          >
            <div>
              <input
                type={props.selectionType === "multiple" ? "checkbox" : "radio"}
                checked={props.selected}
                readOnly
              />
            </div>
          </ListItemInput>
        )}
        <Main align={props.align}>{props.children}</Main>
      </Styled>
    );
  },
);

export default ListItem;
